import _ from 'lodash'
import { Observable, throwError } from 'rxjs'
import { concatMap, first, switchMap } from 'rxjs/operators'

import { Record, RecordLifecycleState } from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { OfflineDataFetcher, AuditRecordOfflineDataFetcher, AuditRecordOfflineDataUpdateCacher } from '@skinsoft/offline'
import { EntityDataStore } from '@skinsoft/entity-data-store'

const NB_TRY_SYNC_UP = 15
const DELAY_TRY_SYNC_UP = 1000

export class RecordDataStore extends EntityDataStore<Record, RecordLifecycleState>{
    public removeOfflineData(id: string): void {
        this.offlineDataUpdateCacher.deleteBeforePushing(id)
    }

    public static getInstance() {
        if (!RecordDataStore.instance) {
            RecordDataStore.instance = new RecordDataStore();
        }

        return RecordDataStore.instance
    }

    public static install(Vue) {
        Vue.prototype.$recordDataStore = RecordDataStore.getInstance()
    }

    private static instance: RecordDataStore = null as any

    constructor() {
        super('recolement', [SEARCH_TYPES.auditRecord])
    }

    public exists(id: Record['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public async transitionRecord(recordId: string, lifecycleState: RecordLifecycleState, lifeCycleTranslations?: {[locale: string]: string}, ignoreCachedStatus: boolean = false): Promise<Record> {
        let forceOfflineUpdate = false
        if(!ignoreCachedStatus) {
            forceOfflineUpdate = await this.hasCachedUpdates(recordId)
        }
        
        if (window.navigator.onLine && !forceOfflineUpdate) {
            return this.proxy
                .fetch(`/recolement`, { id: recordId, lifecycleState }, 'PATCH')
                .pipe(
                    concatMap(() => this.fetchOne(recordId, true)),
                    first(),
                )
                .toPromise();
        } else {
            return this.fetchOne(recordId).pipe(
                first(),
                switchMap(record => {
                    if(record.workflowSchema?.currentState?.name) {
                        _.set(record, 'workflowSchema.currentState.name', lifecycleState)
                    } else {
                        _.set(record, 'workflowSchema.currentState.value', lifecycleState)
                    }

                    if(lifeCycleTranslations) {
                        for(const [locale, value] of Object.entries(lifeCycleTranslations)) {
                            if(record.workflowSchema?.currentState?.[locale]) {
                                _.set(
                                    record,
                                    `workflowSchema.currentState.${locale}`,
                                    value
                                )
                            }
                        }
                    }
                    _.set(record, 'workflowSchema.currentState.availableLifecycleStates', this.getAvailableStatesFromState(lifecycleState))
                    return this.updateOfflineMode(recordId, record)
                }),
                switchMap(() => this.fetchOne(recordId, true)
                ),
            ).toPromise()
        }
    }

    public fetchRandomRecord(offlineItem: boolean = false) {
        if (!window.navigator.onLine || offlineItem) {
            return this.offlineDataFetcher.fetchOneFromPredicate(data => true)
        } else {
            return throwError("This method is not meant to be called while having access to the network")
        }
    }

    public createOfflineRecordForCampaign(itemId: string, itemType: string, auditCampaignId: string): Observable<Record> {
        if(navigator.onLine && !OfflineDataFetcher.isTemporaryId(itemId)) {
            throwError("This method is not meant to be called while having access to the network")
        }

        const newRecord: Partial<Record> = {}

        // link item to recolement
        _.set(newRecord, 'recolementLinkSchema.linkedItem.linkableItem', itemId)
        _.set(newRecord, 'recolementLinkSchema.linkedItem.linkableItemReferenceType', itemType)

        // add a temporary field to store audit campaign
        _.set(newRecord, 'tempOfflineSchema.auditCampaign', auditCampaignId)

        // set status to inProgress
        _.set(newRecord, 'workflowSchema.currentState.value', RecordLifecycleState.IN_PROGRESS)

        return this.createOffline(newRecord)
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new AuditRecordOfflineDataFetcher()
    }

    protected initializeDataPusher() {
        this.offlineDataUpdateCacher = new AuditRecordOfflineDataUpdateCacher()
    }

    private getAvailableStatesFromState(lifecycleState: RecordLifecycleState) {
        switch (lifecycleState) {
            case RecordLifecycleState.IN_PROGRESS:
                return [RecordLifecycleState.TO_VALIDATED, RecordLifecycleState.VALIDATED]
            case RecordLifecycleState.TO_VALIDATED:
                return [RecordLifecycleState.IN_PROGRESS, RecordLifecycleState.VALIDATED]
            case RecordLifecycleState.VALIDATED:
                return [RecordLifecycleState.IN_PROGRESS]
        }

    }

    public addMedia(recordId: string, mediaId: string) {
        return this.proxy.fetch(
            `/recolement/addMedia/${recordId}`,
            {idMedia: mediaId}, 
            'PUT'
        )
    }
}